import React, { useMemo, useCallback } from 'react';
import Autosuggest from 'react-autosuggest';
import styled from 'styled-components';
import StyleConfig from 'src/constants/StyleConfig';
import { InputAutoSuggestFormProps } from 'src/constants/Interface';

const InputAutoSuggest = (props: InputAutoSuggestFormProps) => {
  const {
    inputValue,
    inputType,
    onChange,
    placeholder,
    suggestions,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    onSuggestionSelected,
    renderSuggestion,
    isError,
  } = props;

  const getSuggestionValue = useCallback((value: string) => value, []);

  const wrapRenderSuggestion = useCallback(
    (value: string) => {
      if (!renderSuggestion) {
        return <div>{value}</div>;
      }
      renderSuggestion(value);
    },
    [renderSuggestion],
  );

  const wrapOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        const value = e.currentTarget.value || '';
        onChange(value);
      }
    },
    [onChange],
  );

  const inputProps = useMemo(
    () => ({
      value: inputValue || '',
      onChange: wrapOnChange,
      type: inputType,
      placeholder,
    }),
    [inputValue, inputType, placeholder, wrapOnChange],
  );

  return (
    <Container isError={isError}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={({ value }) => onSuggestionsFetchRequested(value)}
        onSuggestionsClearRequested={() => onSuggestionsClearRequested()}
        getSuggestionValue={(value) => getSuggestionValue(value)}
        onSuggestionSelected={(_, { suggestionValue }) => onSuggestionSelected(suggestionValue)}
        renderSuggestion={wrapRenderSuggestion}
        inputProps={inputProps}
      />
    </Container>
  );
};

InputAutoSuggest.defaultProps = {
  suggestions: [],
  onChange: null,
  onSuggestionsFetchRequested: null,
  wrapOnSuggestionsClearRequested: null,
  renderSuggestion: null,
  inputType: 'text',
  placeholder: '',
  isError: false,
  isDisabeld: false,
};

const Container = styled.div<{ isError: boolean }>`
  box-sizing: border-box;
  position: relative;

  .react-autosuggest__container {
    position: relative;
  }

  .react-autosuggest__input {
    width: 100%;
    border: 1px solid
      ${({ isError }) => (isError ? StyleConfig.COLOR.SECONDORY : StyleConfig.COLOR.INPUT_BORDER)};
    border-radius: 4px;
    background-color: #fff;
    padding: 12px;
    color: #000;
    font-size: 16px;
    line-height: 1;
    height: 52px;
    &::place-holder {
      color: #919191;
    }
  }

  .react-autosuggest__input:focus {
    outline: none;
  }

  .react-autosuggest__container--open .react-autosuggest__input {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .react-autosuggest__suggestions-container {
    position: absolute;
    top: 52px;
    margin: 0;
    padding: 0;
    list-style-type: none;
    border: 1px solid #aaa;
    background-color: #fff;
    font-family: Helvetica, sans-serif;
    font-weight: 300;
    font-size: 16px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    z-index: 2;
  }

  .react-autosuggest__suggestion {
    cursor: pointer;
    padding: 10px 20px;
  }

  .react-autosuggest__suggestion--focused {
    background-color: #ddd;
  }
`;

export default InputAutoSuggest;
